import { colors } from "../../../config/theme";
import { STEPS } from "../../../helpers/constants";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

import {
  GoogleMap,
  MarkerF,
  PolygonF,
  PolylineF,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { GeoMode, LatLng, PlacePoint } from "../../../helpers/enums";
import {
  Maximize,
  Minimize,
  Monitor,
  Moon,
  Smartphone,
  Sun,
  X,
} from "lucide-react";

declare module "leaflet" {
  interface MarkerOptions {
    placeId?: string;
  }
}

const WEB_BASE_URL = (
  process.env.REACT_APP_WEB_URL ||
   "https://staging.venez.ma"
).replace(/\/$/, "");

function activityPageUrl(slug: string) {
  return `${WEB_BASE_URL}/activities/${encodeURIComponent(slug)}`;
}

type PreviewLocale = "fr" | "en" | "ar";
type PreviewTheme = "light" | "dark";

const PREVIEW_LOCALES: { id: PreviewLocale; label: string; flag: string }[] = [
  { id: "fr", label: "FR", flag: "🇫🇷" },
  { id: "ar", label: "AR", flag: "🇲🇦" },
  { id: "en", label: "ENG", flag: "🇬🇧" },
];

function rememberPreviewLocale(locale: PreviewLocale) {
  if (typeof document === "undefined") return;
  document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000; SameSite=Lax`;
}

function previewFrameUrl(
  slug: string,
  locale: PreviewLocale,
  theme: PreviewTheme,
) {
  const path = `${locale}/activities/${encodeURIComponent(slug)}?embed=1&theme=${theme}`;
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
    rememberPreviewLocale(locale);
    return `${window.location.origin}/public-preview/${path}`;
  }
  return `${WEB_BASE_URL}/${path}`;
}

function applyPreviewTheme(frame: HTMLIFrameElement, theme: PreviewTheme) {
  try {
    const doc = frame.contentDocument;
    const view = frame.contentWindow;
    if (!doc?.documentElement || !view) return;
    void doc.location.href;
    view.localStorage.setItem("theme", theme);
    doc.documentElement.classList.remove("light", "dark");
    doc.documentElement.classList.add(theme);
    doc.documentElement.style.colorScheme = theme;
  } catch {
    // The public page is on another origin, so its theme cannot be changed from here.
  }
}

type PreviewDevice = "phone" | "desktop";

const PREVIEW_FRAME_SIZE: Record<
  PreviewDevice,
  { width: string; height: string; radius: number }
> = {
  phone: {
    width: "390px",
    height: "min(844px, calc(100vh - 112px))",
    radius: 28,
  },
  desktop: {
    width: "min(1440px, calc(100vw - 32px))",
    height: "calc(100vh - 112px)",
    radius: 16,
  },
};

function hidePreviewNode(node: HTMLElement) {
  if (node.getAttribute("data-preview-hidden") === "1") return;
  node.setAttribute("data-preview-hidden", "1");
  node.style.setProperty("display", "none", "important");
}

const PREVIEW_DISABLE_LABELS = new Set([
  "continuer",
  "continue",
  "ajouter au panier",
  "add to cart",
  "متابعة",
  "إضافة إلى السلة",
]);

/** Hides navbar, footer, and the booking button when the iframe document is readable. */
function watchActivityPreviewChrome(frame: HTMLIFrameElement) {
  let doc: Document | null = null;
  try {
    doc = frame.contentDocument;
    if (!doc?.body) return () => undefined;
    void doc.location.href;
  } catch {
    return () => undefined;
  }

  const previewDoc = doc;

  const apply = () => {
    previewDoc
      .querySelectorAll(".navbar, footer, .footer, [data-dashboard-suggestions]")
      .forEach((node) => {
      const bar = node as HTMLElement;
      hidePreviewNode(bar);
      if (bar.classList.contains("navbar") && bar.parentElement) {
        hidePreviewNode(bar.parentElement);
      }
    });

    previewDoc.querySelectorAll("h2").forEach((node) => {
      const heading = node.textContent || "";
      if (!/pourriez aussi aimer|might also like|قد يعجبك/i.test(heading)) return;
      const section = node.closest("section") || node.parentElement;
      if (section) hidePreviewNode(section as HTMLElement);
    });

    previewDoc.querySelectorAll("a, button").forEach((node) => {
      const label = (node.textContent || node.getAttribute("aria-label") || "")
        .replace(/\s+/g, " ")
        .trim();
      const normalized = label.toLowerCase();
      if (
        label.length > 0 &&
        label.length < 70 &&
        /disponibil|voir les disponibil|book now|see availab|check availability/i.test(
          label,
        )
      ) {
        hidePreviewNode(node as HTMLElement);
      } else if (PREVIEW_DISABLE_LABELS.has(normalized)) {
        const control = node as HTMLButtonElement;
        control.disabled = true;
        control.setAttribute("aria-disabled", "true");
        control.style.setProperty("pointer-events", "none", "important");
        control.style.setProperty("opacity", "0.55", "important");
        control.style.setProperty("cursor", "not-allowed", "important");
      }
    });
  };

  apply();
  const observer = new MutationObserver(() => apply());
  observer.observe(previewDoc.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

function PreviewDeviceButton({
  active,
  label,
  onClick,
  children,
  width = 42,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
      style={{
        width,
        height: 42,
        borderRadius: 12,
        border: "none",
        background: active ? colors.primary : "#ffffff",
        color: active ? "#ffffff" : "#111827",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: 0.2,
      }}
    >
      {children}
    </button>
  );
}

function PreviewThemeToggle({
  theme,
  onChange,
}: {
  theme: PreviewTheme;
  onChange: (theme: PreviewTheme) => void;
}) {
  const dark = theme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label={dark ? "Mode sombre" : "Mode clair"}
      title={dark ? "Mode sombre" : "Mode clair"}
      onClick={() => onChange(dark ? "light" : "dark")}
      style={{
        width: 76,
        height: 42,
        borderRadius: 21,
        border: "none",
        background: "#ffffff",
        padding: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: dark ? "flex-end" : "flex-start",
        cursor: "pointer",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          background: colors.primary,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease",
        }}
      >
        {dark ? <Moon size={16} /> : <Sun size={16} />}
      </span>
    </button>
  );
}

function PreviewLocaleSelect({
  locale,
  onChange,
}: {
  locale: PreviewLocale;
  onChange: (locale: PreviewLocale) => void;
}) {
  return (
    <select
      value={locale}
      aria-label="Langue de l'aperçu"
      title="Langue"
      onChange={(event) => onChange(event.target.value as PreviewLocale)}
      style={{
        height: 42,
        width: 100,
        borderRadius: 12,
        border: "none",
        background: "#ffffff",
        color: "#111827",
        fontWeight: 700,
        fontSize: 13,
        padding: "0 28px 0 12px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
        cursor: "pointer",
      }}
    >
      {PREVIEW_LOCALES.map((item) => (
        <option key={item.id} value={item.id}>
          {item.flag} {item.label}
        </option>
      ))}
    </select>
  );
}

function PreviewToolbarDivider() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 24,
        background: "rgba(255,255,255,0.35)",
        alignSelf: "center",
      }}
    />
  );
}

/** Scaled preview of the public activity page. Click opens it in a popup. */
export function ActivityPreview({ slug }: { slug?: string | null }) {
  const [open, setOpen] = useState(false);
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [theme, setTheme] = useState<PreviewTheme>("light");
  const [locale, setLocale] = useState<PreviewLocale>("fr");
  const closeRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = frameRef.current;
    if (!frame) return;
    let stop = () => undefined as void;
    const start = () => {
      stop();
      applyPreviewTheme(frame, theme);
      stop = watchActivityPreviewChrome(frame);
    };
    frame.addEventListener("load", start);
    start();
    return () => {
      frame.removeEventListener("load", start);
      stop();
    };
  }, [open, slug, theme]);

  if (!slug) return null;

  const url = activityPageUrl(slug);
  const previewUrl = previewFrameUrl(slug, locale, theme);
  const frameSize = PREVIEW_FRAME_SIZE[device];

  return (
    <>
      <div
        className="position-relative overflow-hidden bg-white"
        style={{
          width: 188,
          height: 98,
          borderRadius: 10,
          border: "1px solid #e6e6e6",
          flexShrink: 0,
        }}
      >
        <iframe
          src={url}
          title="Aperçu de l'activité"
          tabIndex={-1}
          loading="lazy"
          style={{
            width: 1254,
            height: 654,
            border: 0,
            transform: "scale(0.15)",
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Ouvrir l'aperçu"
          aria-label="Ouvrir l'aperçu de l'activité"
          className="position-absolute top-0 start-0 w-100 h-100 border-0 bg-transparent"
          style={{ cursor: "pointer" }}
        />
      </div>

      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Aperçu de l'activité"
            onClick={() => setOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 3000,
              background: "rgba(17, 24, 39, 0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 28,
            }}
          >
            <button
              ref={closeRef}
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer l'aperçu"
              title="Fermer"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "none",
                background: "#111827",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 1,
              }}
            >
              <X size={18} />
            </button>

            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                maxWidth: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PreviewDeviceButton
                  active={device === "phone"}
                  label="Aperçu téléphone"
                  onClick={() => setDevice("phone")}
                >
                  <Smartphone size={18} />
                </PreviewDeviceButton>
                <PreviewDeviceButton
                  active={device === "desktop"}
                  label="Aperçu ordinateur"
                  onClick={() => setDevice("desktop")}
                >
                  <Monitor size={18} />
                </PreviewDeviceButton>
                <PreviewToolbarDivider />
                <PreviewThemeToggle theme={theme} onChange={setTheme} />
                <PreviewLocaleSelect locale={locale} onChange={setLocale} />
              </div>

              <div
                style={{
                  width: frameSize.width,
                  height: frameSize.height,
                  background: "#fff",
                  borderRadius: frameSize.radius,
                  overflow: "hidden",
                  boxShadow: "0 24px 80px rgba(0, 0, 0, 0.35)",
                  border: device === "phone" ? "8px solid #111827" : "none",
                }}
              >
                <iframe
                  key={previewUrl}
                  ref={frameRef}
                  src={previewUrl}
                  title="Aperçu de l'activité"
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function StepIndicator({
  currentStep,
  furthestStep,
  onStepClick,
  isFullyEditable = true,
}: {
  currentStep: number;
  furthestStep: number;
  onStepClick: (id: number) => void;
  isFullyEditable?: boolean;
}) {
  const totalSteps = STEPS.length;
  const lineStartPercent = 100 / totalSteps / 2;
  const lineEndPercent = 100 - lineStartPercent;
  const lineWidthPercent = lineEndPercent - lineStartPercent;

  // If not editable, progress bar stays at current step
  const progressWidthPercent = isFullyEditable
    ? ((currentStep - 1) / (totalSteps - 1)) * lineWidthPercent
    : ((currentStep - 1) / (totalSteps - 1)) * lineWidthPercent;

  return (
    <div className="step-indicator-wrapper mb-4">
      <div
        className="d-flex justify-content-between position-relative"
        style={{ paddingTop: "20px" }}
      >
        {/* ... (keep your line divs exactly as they are) ... */}
        <div
          className="position-absolute"
          style={{
            top: "26%",
            left: `${lineStartPercent}%`,
            width: `${lineWidthPercent}%`,
            height: "3px",
            backgroundColor: "#e6e6e6",
            zIndex: 0,
          }}
        />
        <div
          className="position-absolute"
          style={{
            top: "26%",
            left: `${lineStartPercent}%`,
            width: `${progressWidthPercent}%`,
            height: "3px",
            backgroundColor: colors.yellow,
            zIndex: 1,
            transition: "width 0.35s ease-in-out",
          }}
        />

        {STEPS.map((step) => {
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isFuture = step.id > currentStep;

          const isClickable =
            isFullyEditable && step.id <= furthestStep && !isCurrent;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              className="d-flex flex-column align-items-center position-relative bg-transparent border-0 p-0"
              style={{
                zIndex: 2,
                flex: 1,
                // 👇 Change cursor based on editability
                cursor: isClickable ? "pointer" : "not-allowed",
              }}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`Étape ${step.id} : ${step.label}`}
            >
              {/* ... (keep your inner divs exactly as they are) ... */}
              <div
                className="d-flex align-items-center justify-content-center mb-2"
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  backgroundColor:
                    isCompleted || isCurrent ? colors.primary : "#ffffff",
                  border: `2px solid ${isFuture ? "#d8d8d8" : colors.primary}`,
                  boxShadow: isCurrent
                    ? `0 4px 14px rgba(78, 205, 196, 0.45)`
                    : "none",
                  transition: "all 0.25s ease",
                }}
              >
                {isCompleted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="#ffffff"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: isCurrent ? "#fff" : "#aaa",
                    }}
                  >
                    {step.id}
                  </span>
                )}
              </div>

              <div
                className="text-center"
                style={{
                  fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                  color: isFuture ? "#999" : "#2b2b2b",
                  fontSize: "0.9rem",
                }}
              >
                {step.label}
              </div>
              <div
                className="text-center d-none d-md-block"
                style={{
                  fontSize: "0.76rem",
                  color: isFuture ? "#bbb" : "#828282",
                  maxWidth: "120px",
                  marginTop: "2px",
                }}
              >
                {step.description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Field({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <label className="d-flex justify-content-between align-items-baseline mb-1">
        <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
          {label} {required && <span style={{ color: "#e05252" }}>*</span>}
        </span>
        {hint && (
          <span style={{ fontSize: "0.76rem", color: "#9a9a9a" }}>{hint}</span>
        )}
      </label>
      {children}
      {error && (
        <div className="small mt-1" style={{ color: "#e05252" }}>
          {error}
        </div>
      )}
    </div>
  );
}

export function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="mb-4 p-4"
      style={{
        border: "1px solid #ececec",
        borderRadius: "14px",
        background: "#fff",
      }}
    >
      <div className="mb-3">
        <h6 className="m-0" style={{ fontWeight: 700 }}>
          {title}
        </h6>
        {subtitle && <div className="small text-muted mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

const LIBRARIES: ("places" | "drawing")[] = ["places", "drawing"];
const MOROCCO_CENTER = { lat: 31.7917, lng: -7.0926 };

export function PickupLocationMap({
  city,
  mode,
  places,
  onAddPlace,
  onRemovePlace,
  polygon,
  onPolygonChange,
}: {
  city?: { lat: number; lng: number } | null;
  mode: GeoMode;
  places: PlacePoint[];
  onAddPlace: (lat: number, lng: number, name?: string) => void;
  onRemovePlace: (id: string) => void;
  polygon: LatLng[];
  onPolygonChange: (polygon: LatLng[]) => void;
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAP_KEY as string}`,
    libraries: LIBRARIES,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const activePolygonsRef = useRef<Set<google.maps.Polygon>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoCenteredRef = useRef(false);

  // Manual zone-drawing state (replaces DrawingManager)
  const [drawingPoints, setDrawingPoints] = useState<LatLng[]>([]);
  const isDrawing = mode === "zones" && polygon.length === 0;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const center = city || MOROCCO_CENTER;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === mapWrapperRef.current);
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, "resize");
        mapRef.current.setCenter(center);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        document.fullscreenElement === mapWrapperRef.current
      ) {
        e.stopPropagation();
        // don't preventDefault: let the browser still exit fullscreen natively
      }
    };
    // capture: true so this runs before the modal's own Esc listener
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!mapWrapperRef.current) return;

    if (!document.fullscreenElement) {
      mapWrapperRef.current.requestFullscreen?.().catch(() => {
        // Fullscreen request can be rejected (e.g. no user gesture, unsupported) — ignore silently
      });
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (city) {
      map.panTo(city);
      map.setZoom(12);
    } else if (mode === "points" && places.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds);
      if (places.length === 1) {
        map.setZoom(15);
      }
      hasAutoCenteredRef.current = true;
    } else if (mode === "zones" && polygon.length >= 3) {
      const bounds = new google.maps.LatLngBounds();
      polygon.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds);
      hasAutoCenteredRef.current = true;
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;
    if (city) {
      mapRef.current.panTo(city);
      mapRef.current.setZoom(12);
    }
  }, [city]);

  // Fit bounds when initial places or polygon load asynchronously without a selected city
  useEffect(() => {
    if (!mapRef.current || city || hasAutoCenteredRef.current) return;
    if (mode === "points" && places.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapRef.current.fitBounds(bounds);
      if (places.length === 1) {
        mapRef.current.setZoom(15);
      }
      hasAutoCenteredRef.current = true;
    } else if (mode === "zones" && polygon.length >= 3) {
      const bounds = new google.maps.LatLngBounds();
      polygon.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapRef.current.fitBounds(bounds);
      hasAutoCenteredRef.current = true;
    }
  }, [places, polygon, mode, city]);

  // Reset any in-progress drawing and cleanup polygon overlays when switching modes
  useEffect(() => {
    setDrawingPoints([]);
    if (mode !== "zones") {
      activePolygonsRef.current.forEach((poly) => {
        google.maps.event.clearInstanceListeners(poly);
        const paths = poly.getPaths();
        if (paths) google.maps.event.clearInstanceListeners(paths);
        const path = poly.getPath();
        if (path) google.maps.event.clearInstanceListeners(path);
        poly.setMap(null);
      });
      activePolygonsRef.current.clear();
      polygonRef.current = null;
    }
  }, [mode]);

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    if (mode === "points") {
      onAddPlace(e.latLng.lat(), e.latLng.lng());
      return;
    }

    if (mode === "zones" && isDrawing) {
      setDrawingPoints((prev) => [
        ...prev,
        { lat: e.latLng!.lat(), lng: e.latLng!.lng() },
      ]);
    }
  };

  const finishDrawing = () => {
    if (drawingPoints.length < 3) return;
    onPolygonChange(drawingPoints);
    setDrawingPoints([]);
  };

  const cancelDrawing = () => {
    setDrawingPoints([]);
  };

  const undoLastPoint = () => {
    setDrawingPoints((prev) => prev.slice(0, -1));
  };

  // ---- Polygon (zones mode) ----

  const extractPolygonCoords = (poly: google.maps.Polygon): LatLng[] => {
    const path = poly.getPath();
    if (!path) return [];
    const coords: LatLng[] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const pt = path.getAt(i);
      coords.push({ lat: pt.lat(), lng: pt.lng() });
    }
    return coords;
  };

  const syncPolygon = useCallback(() => {
    if (!polygonRef.current) return;
    const coords = extractPolygonCoords(polygonRef.current);
    if (coords.length >= 3) {
      onPolygonChange(coords);
    }
  }, [onPolygonChange]);

  const handlePathChange = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      syncPolygon();
    }, 150);
  }, [syncPolygon]);

  const handleMouseUp = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    syncPolygon();
  }, [syncPolygon]);

  const attachPathListeners = useCallback(
    (poly: google.maps.Polygon) => {
      const bind = () => {
        const path = poly.getPath();
        if (!path) return;
        google.maps.event.clearInstanceListeners(path);
        path.addListener("set_at", handlePathChange);
        path.addListener("insert_at", handlePathChange);
        path.addListener("remove_at", handlePathChange);
      };

      const paths = poly.getPaths();
      if (paths) {
        google.maps.event.clearInstanceListeners(paths);
        paths.addListener("set_at", bind);
        paths.addListener("insert_at", bind);
        paths.addListener("remove_at", bind);
      }

      bind();
    },
    [handlePathChange],
  );

  const onExistingPolygonLoad = useCallback(
    (poly: google.maps.Polygon) => {
      activePolygonsRef.current.add(poly);
      polygonRef.current = poly;
      attachPathListeners(poly);
    },
    [attachPathListeners],
  );

  const onExistingPolygonUnmount = useCallback((poly: google.maps.Polygon) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    google.maps.event.clearInstanceListeners(poly);
    const paths = poly.getPaths();
    if (paths) google.maps.event.clearInstanceListeners(paths);
    const path = poly.getPath();
    if (path) google.maps.event.clearInstanceListeners(path);
    poly.setMap(null);
    activePolygonsRef.current.delete(poly);
    if (polygonRef.current === poly) {
      polygonRef.current = null;
    }
  }, []);

  const clearZone = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    activePolygonsRef.current.forEach((poly) => {
      google.maps.event.clearInstanceListeners(poly);
      const paths = poly.getPaths();
      if (paths) google.maps.event.clearInstanceListeners(paths);
      const path = poly.getPath();
      if (path) google.maps.event.clearInstanceListeners(path);
      poly.setMap(null);
    });
    activePolygonsRef.current.clear();
    if (polygonRef.current) {
      google.maps.event.clearInstanceListeners(polygonRef.current);
      const paths = polygonRef.current.getPaths();
      if (paths) google.maps.event.clearInstanceListeners(paths);
      const path = polygonRef.current.getPath();
      if (path) google.maps.event.clearInstanceListeners(path);
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setDrawingPoints([]);
    onPolygonChange([]);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // ---- Points (places mode) ----

  const onMarkerRightClick = (p: PlacePoint) => {
    onRemovePlace(p.id);
  };

  if (!isLoaded) {
    return <div className="text-muted small">Chargement de la carte...</div>;
  }

  return (
    <div>
      {mode === "points" && (
        <PlaceSearchBox
          city={city}
          onSelect={(place) => {
            onAddPlace(place.lat, place.lng, place.name);
            mapRef.current?.panTo({ lat: place.lat, lng: place.lng });
            mapRef.current?.setZoom(15);
          }}
        />
      )}

      <div
        ref={mapWrapperRef}
        style={{
          position: "relative",
          borderRadius: isFullscreen ? 0 : "10px",
          overflow: "hidden",
          border: isFullscreen ? "none" : "1px solid #ececec",
          width: "100%",
          height: isFullscreen ? "100vh" : "460px",
        }}
      >
        <button
          type="button"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
          className="btn btn-sm btn-light"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          }}
        >
          {isFullscreen ? <Minimize /> : <Maximize />}
        </button>

        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={city ? 12 : 5.5}
          onLoad={onMapLoad}
          onClick={onMapClick}
          onMouseUp={handleMouseUp}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {mode === "points" &&
            places.map((p) => (
              <MarkerF
                key={p.id}
                position={{ lat: p.lat, lng: p.lng }}
                title={p.name || "Point de rendez-vous"}
                onRightClick={() => onMarkerRightClick(p)}
              />
            ))}

          {mode === "zones" && polygon.length >= 3 && (
            <PolygonF
              key="active-zone-polygon"
              paths={polygon}
              options={{
                fillColor: colors.yellow,
                fillOpacity: 0.2,
                strokeColor: colors.yellow,
                strokeWeight: 2,
                editable: true,
                draggable: false,
              }}
              onLoad={onExistingPolygonLoad}
              onUnmount={onExistingPolygonUnmount}
              onMouseUp={handleMouseUp}
              onDragEnd={handleMouseUp}
            />
          )}

          {/* In-progress manual drawing */}
          {mode === "zones" && isDrawing && drawingPoints.length > 0 && (
            <>
              <PolylineF
                path={drawingPoints}
                options={{
                  strokeColor: colors.yellow,
                  strokeWeight: 2,
                }}
              />
              {drawingPoints.map((pt, i) => (
                <MarkerF
                  key={`draw-pt-${i}`}
                  position={pt}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: colors.yellow,
                    fillOpacity: 1,
                    strokeColor: "#fff",
                    strokeWeight: 1,
                  }}
                />
              ))}
            </>
          )}
        </GoogleMap>
      </div>

      <div className="small text-muted mt-2">
        {mode === "points"
          ? "Recherchez un lieu, ou cliquez sur la carte pour ajouter un point. Clic droit sur un marqueur pour le retirer."
          : isDrawing
            ? "Cliquez sur la carte pour placer les sommets de la zone (3 minimum), puis validez."
            : "Faites glisser les sommets pour ajuster la zone."}
      </div>

      {mode === "zones" && isDrawing && drawingPoints.length > 0 && (
        <div className="d-flex gap-2 mt-2">
          <button
            type="button"
            className="btn btn-sm"
            style={{
              backgroundColor: colors.yellow,
              color: "#fff",
              border: "none",
            }}
            onClick={finishDrawing}
            disabled={drawingPoints.length < 3}
          >
            Valider la zone ({drawingPoints.length} points)
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={undoLastPoint}
          >
            Annuler le dernier point
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link text-danger"
            onClick={cancelDrawing}
          >
            Recommencer
          </button>
        </div>
      )}

      {mode === "zones" && polygon.length > 0 && (
        <button
          type="button"
          className="btn btn-sm btn-link text-danger p-0 mt-1"
          onClick={clearZone}
        >
          Effacer la zone et recommencer
        </button>
      )}

      {mode === "points" && places.length > 0 && (
        <ul className="list-unstyled mt-2 mb-0">
          {places.map((p) => (
            <li
              key={p.id}
              className="d-flex align-items-center justify-content-between py-1 small"
            >
              <span>
                📍 {p.name || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-link text-danger p-0"
                onClick={() => onRemovePlace(p.id)}
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
export function buildGeoData(
  mode: GeoMode,
  places: PlacePoint[],
  polygon: LatLng[],
): any {
  if (mode === "zones") {
    if (polygon.length < 3) return null;
    const ring = [...polygon, polygon[0]].map((p) => [p.lng, p.lat]);
    return {
      mode: "zone",
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: {},
    };
  }
  if (places.length === 0) return null;
  return {
    mode: "points",
    type: "FeatureCollection",
    features: places.map((p) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { name: p.name || "" },
    })),
  };
}

export function parseGeoData(geojson: any): {
  mode: GeoMode;
  places: PlacePoint[];
  polygon: LatLng[];
} {
  if (!geojson) return { mode: "points", places: [], polygon: [] };

  if (geojson.mode === "zone" && geojson.geometry?.coordinates?.[0]) {
    const ring = geojson.geometry.coordinates[0];
    const polygon = ring
      .slice(0, -1)
      .map((c: number[]) => ({ lng: c[0], lat: c[1] }));
    return { mode: "zones", places: [], polygon };
  }

  if (geojson.mode === "points" && geojson.features) {
    const places = geojson.features.map((f: any, i: number) => ({
      id: `existing-${i}`,
      lat: f.geometry.coordinates[1],
      lng: f.geometry.coordinates[0],
      name: f.properties?.name,
    }));
    return { mode: "points", places, polygon: [] };
  }

  // backward-compat with the earlier single-point {type:"Point",...} format
  if (geojson.type === "Point" && geojson.coordinates) {
    return {
      mode: "points",
      places: [
        {
          id: "existing-0",
          lat: geojson.coordinates[1],
          lng: geojson.coordinates[0],
        },
      ],
      polygon: [],
    };
  }

  return { mode: "points", places: [], polygon: [] };
}

export function PlaceSearchBox({
  city,
  onSelect,
  placeholder = "Rechercher un hôtel, une adresse...",
}: {
  city?: { lat: number; lng: number } | null;
  onSelect: (place: { lat: number; lng: number; name: string }) => void;
  placeholder?: string;
}) {
  const [searchText, setSearchText] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onAutocompleteLoad = (ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
    ac.setComponentRestrictions({ country: "ma" });
  };

  useEffect(() => {
    if (!autocompleteRef.current || !city) return;
    const circle = new google.maps.Circle({ center: city, radius: 20000 });
    autocompleteRef.current.setBounds(
      circle.getBounds() as google.maps.LatLngBounds,
    );
  }, [city]);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.geometry?.location) return;

    onSelect({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      name: place.name || place.formatted_address || "",
    });
    setSearchText("");
  };

  return (
    <div className="position-relative mb-2">
      <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Autocomplete>
    </div>
  );
}

export function MapReady({ onReady }: { onReady: () => void }) {
  useEffect(() => {
    onReady();
  }, [onReady]);
  return null;
}
